import React, { Component } from 'react';
import { Alert, Breadcrumb, Button, Card, Col, Descriptions, Spin, Tag, notification } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { SubmissionError } from 'redux-form';
import moment from 'moment';

import AntIcon from '../shared/components/AntIcon';
import CampaignForm from './components/CampaignForm';
import CampaignStatsCard from './components/CampaignStatsCard';
import { getApiErrorMessage, getGiveawayCampaignWithFallback, updateGiveawayCampaign, formatGiveawayCampaignLoadError } from './api';
import { pickCampaignFormValues } from './campaignFormValues';
import { getGiveawayStatusColor } from './status';
import { formatWinnerProgress, getRequiredWinnerCount, isSelectionComplete } from './winners';
import WinnerInfosDisplay from './components/WinnerInfosDisplay';

class EditCampaign extends Component {
  state = {
    actionLoading: '',
    campaign: null,
    loadingCampaign: true,
    loadError: '',
  };

  componentDidMount() {
    this.loadCampaignContext();
  }

  getCampaignId() {
    return this.props.match.params.id;
  }

  loadCampaignContext = () => {
    const id = this.getCampaignId();
    this.setState({ loadingCampaign: true, loadError: '' });
    return getGiveawayCampaignWithFallback(id)
      .then((campaign) => {
        this.setState({
          campaign,
          loadingCampaign: false,
          loadError: '',
        });
      })
      .catch((error) => {
        this.setState({
          campaign: null,
          loadingCampaign: false,
          loadError: formatGiveawayCampaignLoadError(error),
        });
      });
  }

  handleSubmit = (data) => {
    const id = this.getCampaignId();
    const record = pickCampaignFormValues(data);
    return updateGiveawayCampaign(id, record)
      .then(() => this.loadCampaignContext())
      .then(() => {
        const { campaign } = this.state;
        notification.success({
          message: 'Campaign updated',
          description: `${campaign ? campaign.title : 'Campaign'} has been saved.`,
        });
      })
      .catch((error) => {
        throw new SubmissionError({
          _error: getApiErrorMessage(error, 'Failed to update giveaway campaign.'),
        });
      });
  }

  handleStatusAction = (status) => {
    const id = this.getCampaignId();
    this.setState({ actionLoading: status });
    return updateGiveawayCampaign(id, { status })
      .then(() => this.loadCampaignContext())
      .then(() => {
        const { campaign } = this.state;
        notification.success({
          message: 'Status updated',
          description: `${campaign ? campaign.title : 'Campaign'} is now ${campaign ? campaign.status : status}.`,
        });
      })
      .catch((error) => {
        notification.error({
          message: 'Status update failed',
          description: getApiErrorMessage(error, 'Unable to change giveaway status.'),
        });
      })
      .finally(() => {
        this.setState({ actionLoading: '' });
      });
  }

  renderCampaignMeta() {
    const { campaign } = this.state;
    if (!campaign) return null;
    const complete = isSelectionComplete(campaign);

    return (
      <Card title="Campaign details" size="small" style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Campaign ID">{campaign._id}</Descriptions.Item>
          <Descriptions.Item label="Title">{campaign.title || '-'}</Descriptions.Item>
          <Descriptions.Item label="Slug Month">{campaign.slugMonth || '-'}</Descriptions.Item>
          <Descriptions.Item label="Number of winners">
            {getRequiredWinnerCount(campaign)}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getGiveawayStatusColor(campaign.status)}>{campaign.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Selection Date (UTC)">
            {campaign.selectionDateUtc
              ? moment.utc(campaign.selectionDateUtc).format('YYYY-MM-DD HH:mm [UTC]')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Banner Image">
            {campaign.bannerImage ? (
              <a href={campaign.bannerImage} target="_blank" rel="noopener noreferrer">
                Open banner
              </a>
            ) : (
              'Not set'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Shoes Image">
            {campaign.shoesImage ? (
              <div>
                <a href={campaign.shoesImage} target="_blank" rel="noopener noreferrer">
                  Open shoes image
                </a>
                <div style={{ marginTop: 8 }}>
                  <img
                    src={campaign.shoesImage}
                    alt="Shoes"
                    style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4 }}
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>
            ) : (
              'Not set'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {campaign.updatedAt ? moment.utc(campaign.updatedAt).format('YYYY-MM-DD HH:mm [UTC]') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Winners">
            <WinnerInfosDisplay campaign={campaign} showProgress={false} />
          </Descriptions.Item>
          <Descriptions.Item label="Winner progress">
            <Tag color={complete ? 'blue' : 'orange'}>{formatWinnerProgress(campaign)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Selection complete">
            {complete ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }

  render() {
    const { actionLoading, campaign, loadingCampaign, loadError } = this.state;

    return (
      <div id="giveaway-edit">
        <Breadcrumb>
          <Breadcrumb.Item><Link to="/giveaway">Giveaway</Link></Breadcrumb.Item>
          <Breadcrumb.Item>Edit Campaign</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          {loadingCampaign ? (
            <>
              <CampaignStatsCard loading />
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            </>
          ) : loadError ? (
            <>
              <CampaignStatsCard campaign={null} loading={false} />
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Campaign data unavailable"
                description={loadError}
              />
            </>
          ) : (
            <>
              <CampaignStatsCard campaign={campaign} loading={false} />
              {this.renderCampaignMeta()}
              <CampaignForm
                key={campaign._id}
                initialValues={pickCampaignFormValues(campaign)}
                onSubmit={this.handleSubmit}
                cancelPath="/giveaway"
                extraActions={(
                  <>
                    <Col>
                      <Button>
                        <Link to={`/giveaway/${campaign._id}/participants`}>
                          <AntIcon type="team" /> Participants
                        </Link>
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        loading={actionLoading === 'active'}
                        disabled={campaign.status === 'active'}
                        onClick={() => this.handleStatusAction('active')}
                      >
                        Activate
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        loading={actionLoading === 'closed'}
                        disabled={campaign.status === 'closed'}
                        onClick={() => this.handleStatusAction('closed')}
                      >
                        Close
                      </Button>
                    </Col>
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
    );
  }
}

export default connect(null, null)(EditCampaign);
